/**
 * Command Center — Financials widget.
 *
 * Headline P&L / win-rate stats come straight from the command-center snapshot's
 * `financials` quadrant (titan_reporting_api.get_summary — broker-sourced). The
 * equity curve is fetched separately from the already-deployed /equity-curve
 * endpoint on a SLOW cadence (30s): the curve changes only on trade close, so it
 * would be wasteful to carry its full point array in the 1.5s telemetry snapshot.
 *
 * Reuses the existing StatCard + EquityCurve components — no new charting dep.
 */

import React, { useEffect, useState } from 'react';
import { DollarSign, Percent, Hash, CalendarDays } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import StatCard from '../StatCard';
import EquityCurve from '../reports/EquityCurve';
import { reportingApi } from '../../lib/api';
import type { CommandCenterQuadrant } from '../../lib/api';

const EQUITY_REFRESH_MS = 30_000; // curve moves only on trade close — slow refresh

const fmtUsd = (v: number): string => {
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const num = (q: Record<string, unknown> | undefined, key: string): number => Number((q?.[key] as number) ?? 0);

type CurvePoint = { timestamp: string; cumulative_pnl: number; realized_pnl: number; symbol: string };

const FinancialsWidget: React.FC<{ data: CommandCenterQuadrant | undefined }> = ({ data }) => {
  const errored = !!data && typeof data.error === 'string';
  const q = (data as Record<string, unknown> | undefined) ?? {};

  const totalPnl = num(q, 'total_pnl');
  const winRate = num(q, 'win_rate');
  const totalTrades = num(q, 'total_trades');
  const today = (q.today as Record<string, unknown> | undefined) ?? {};
  const todayPnl = Number((today.pnl as number) ?? 0);

  const [curve, setCurve] = useState<CurvePoint[]>([]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await reportingApi.equityCurve(30);
        if (!mounted) return;
        // The shared EquityCurve component wants {timestamp, cumulative_pnl,
        // realized_pnl, symbol}; the endpoint returns the first two — pad the rest.
        setCurve(
          (res.points || []).map((p) => ({
            timestamp: p.timestamp,
            cumulative_pnl: p.cumulative_pnl,
            realized_pnl: 0,
            symbol: '',
          })),
        );
      } catch {
        /* keep last curve; financial stats above are unaffected */
      }
    };
    void load();
    const t = setInterval(load, EQUITY_REFRESH_MS);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <DollarSign className="h-4 w-4" /> Financials
        </CardTitle>
        <Badge variant={errored ? 'destructive' : 'secondary'}>{errored ? 'degraded' : 'live'}</Badge>
      </CardHeader>
      <CardContent className="flex-1 space-y-3">
        {errored ? (
          <div className="text-sm text-destructive">{String(data?.error)}</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <StatCard
                title={`Total P&L (${num(q, 'period_days') || 7}d)`}
                value={fmtUsd(totalPnl)}
                variant={totalPnl >= 0 ? 'success' : 'error'}
                icon={<DollarSign className="h-4 w-4" />}
              />
              <StatCard
                title="Win Rate"
                value={winRate.toFixed(1)}
                suffix="%"
                variant={winRate >= 50 ? 'success' : 'warning'}
                icon={<Percent className="h-4 w-4" />}
              />
              <StatCard
                title="Trades"
                value={totalTrades}
                icon={<Hash className="h-4 w-4" />}
              />
              <StatCard
                title="Today P&L"
                value={fmtUsd(todayPnl)}
                variant={todayPnl >= 0 ? 'success' : 'error'}
                icon={<CalendarDays className="h-4 w-4" />}
              />
            </div>
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                Equity curve (30d)
              </div>
              <EquityCurve points={curve} height={140} />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default FinancialsWidget;
